import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip
} from '@mui/material';
import instance from '../../utils/axiosConfig';
import { getProvinces, getCities } from '../../constants/locations';

function CreateUnclaimedDistributor() {
  const [formData, setFormData] = useState({
    name: '',
    governmentName: '',
    CUIT: '',
    email: '',
    password: '',
    description: '',
    country: 'AR',
    province: '',
    city: '',
    address: '',
    phone: '',
    zones: []
  });

  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);
  const [newZone, setNewZone] = useState('');

  const predefinedZones = [
    'Todo el país',
    'GBA Norte',
    'GBA Sur',
    'GBA Oeste',
    'CABA',
    'Provincia de Buenos Aires',
    'Provincia de Córdoba',
    'Provincia de Santa Fe',
    'Provincia de Mendoza',
    'Provincia de Tucumán',
    'Provincia de Entre Ríos',
    'Provincia de Salta',
    'Provincia de Misiones',
    'Provincia de Chaco',
    'Provincia de Corrientes',
    'Provincia de Santiago del Estero',
    'Provincia de San Juan',
    'Provincia de Jujuy',
    'Provincia de Río Negro',
    'Provincia de Neuquén',
    'Provincia de Formosa',
    'Provincia de Chubut',
    'Provincia de San Luis',
    'Provincia de Catamarca',
    'Provincia de La Rioja',
    'Provincia de La Pampa',
    'Provincia de Santa Cruz',
    'Provincia de Tierra del Fuego',
    'Costa Atlántica',
    'Cuyo',
    'NOA',
    'NEA',
    'Patagonia Norte',
    'Patagonia Sur'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage({ type: '', text: '' });
  };

  const handleAddZone = (selectedZone) => {
    if (selectedZone === 'Todo el país') {
      setFormData(prev => ({
        ...prev,
        zones: ['Todo el país']
      }));
      setNewZone('');
    } else if (selectedZone && !formData.zones.includes(selectedZone)) {
      const newZones = formData.zones.filter(zone => zone !== 'Todo el país');
      setFormData(prev => ({
        ...prev,
        zones: [...newZones, selectedZone]
      }));
      setNewZone('');
    }
  };

  const handleRemoveZone = (zoneToRemove) => {
    setFormData(prev => ({
      ...prev,
      zones: prev.zones.filter(zone => zone !== zoneToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await instance.post('/api/moderator-management/create-unclaimed-distributor', formData);
      setMessage({
        type: 'success',
        text: 'Distribuidor no reclamado creado exitosamente'
      });
      // Reset form
      setFormData({
        name: '',
        governmentName: '',
        CUIT: '',
        email: '',
        password: '',
        description: '',
        country: 'AR',
        province: '',
        city: '',
        address: '',
        phone: '',
        zones: []
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Error al crear distribuidor no reclamado'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Crear Distribuidor No Reclamado
        </Typography>

        {message.text && (
          <Alert severity={message.type} sx={{ mb: 2 }}>
            {message.text}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Razón Social"
                name="governmentName"
                value={formData.governmentName}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CUIT"
                name="CUIT"
                value={formData.CUIT}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contraseña"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>País</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="AR">Argentina</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Provincia</InputLabel>
                <Select
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  required
                >
                  {getProvinces(formData.country).map((province) => (
                    <MenuItem key={province} value={province}>
                      {province}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Ciudad</InputLabel>
                <Select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  disabled={!formData.province}
                >
                  {getCities(formData.country, formData.province).map((city) => (
                    <MenuItem key={city} value={city}>
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Teléfono"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>¿A qué zonas enviás pedidos?</InputLabel>
                  <Select
                    value={newZone}
                    label="¿A qué zonas enviás pedidos?"
                    onChange={(e) => handleAddZone(e.target.value)}
                    disabled={formData.zones.includes('Todo el país')}
                  >
                    {predefinedZones
                      .filter(zone => {
                        if (zone === 'Todo el país') {
                          return formData.zones.length === 0;
                        }
                        return !formData.zones.includes(zone) && !formData.zones.includes('Todo el país');
                      })
                      .map((zone) => (
                        <MenuItem key={zone} value={zone}>
                          {zone}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.zones.map((zone, index) => (
                  <Chip
                    key={index}
                    label={zone}
                    onDelete={() => handleRemoveZone(zone)}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Crear Distribuidor'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}

export default CreateUnclaimedDistributor; 